import { api } from '@/lib/api'
import { downloadMode, telegramWebApp } from '@/lib/telegram'

/** Hand a fetched file (api.blob) to the browser as a download. Browser only. */
export function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  // Revoke after the click has been handled (some browsers read the URL asynchronously).
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}

/** POST /exports/ticket: a one-time (120 s) link the Telegram client can fetch without our
 *  bearer token (src/api/admin/exports.py). */
interface ExportTicket {
  url: string
  file_name: string
  expires_in: number
}

export interface ExportResult {
  /** the server cut the file at its row cap (X-Export-Truncated: 1); unknown inside Telegram */
  truncated: boolean
  /** false when the operator declined Telegram's download popup */
  accepted: boolean
}

/** The truncation header of a CSV export (pure, for tests). */
export function isTruncated(headers: Pick<Headers, 'get'>): boolean {
  return headers.get('X-Export-Truncated') === '1'
}

/** Download a CSV export (`path` relative to /api/admin/v1, with its filters).
 *
 *  In a browser: api.blob (bearer token, refresh-once) → saveBlob. Inside Telegram a blob link
 *  does not download, so: a one-time ticket, then `Telegram.WebApp.downloadFile` (Bot API 8.0,
 *  core.telegram.org/bots/webapps «downloadFile») or, on an older client, `openLink` (the
 *  external browser downloads the ticket URL). */
export async function downloadExport(path: string, fileName: string): Promise<ExportResult> {
  const app = telegramWebApp()
  const mode = downloadMode(app)
  if (mode === 'blob' || app === null) {
    const { blob, headers } = await api.blobWithHeaders(path)
    saveBlob(blob, fileName)
    return { truncated: isTruncated(headers), accepted: true }
  }
  const ticket = await api.post<ExportTicket>('/exports/ticket', { path })
  const url = new URL(ticket.url, window.location.origin).toString()
  if (mode === 'native' && app.downloadFile) {
    const accepted = await new Promise<boolean>((resolve) => {
      app.downloadFile?.({ url, file_name: ticket.file_name || fileName }, (ok) => resolve(ok !== false))
    })
    return { truncated: false, accepted }
  }
  app.openLink(url)
  return { truncated: false, accepted: true }
}
